<template>
    <div class="automation-general-insights">
        <div class="insights-container">
            <div class="header-container">
                <div class="title">{{ $t("automation.insightsPage.title") }}</div>
                <div class="filter-container">
                    <div class="filter-item">
                        <a-tooltip :title="$t('automation.insightsPage.selectAutomation')">
                            <i class="material-icons filter-icon">settings_suggest</i>
                        </a-tooltip>
                        <a-select
                            v-model="filters.automationId"
                            :placeholder="$t('automation.insightsPage.selectAutomation')"
                            style="width: 220px"
                            :allowClear="true"
                            @change="handleAutomationChange"
                        >
                            <a-select-option v-for="automation in automations" :key="automation._id" :value="automation._id">
                                {{ automation.name }}
                            </a-select-option>
                        </a-select>
                    </div>

                    <div class="filter-item">
                        <a-tooltip :title="$t('automation.insightsPage.dateRange')">
                            <i class="material-icons filter-icon">date_range</i>
                        </a-tooltip>
                        <a-range-picker
                            v-model="filters.dateRange"
                            :format="'DD/MM/YYYY'"
                            :placeholder="[$t('automation.insightsPage.datePlaceholderStart'), $t('automation.insightsPage.datePlaceholderEnd')]"
                            style="width: 220px"
                            :ranges="dateRangePresets"
                            @change="handleDateChange"
                        />
                    </div>

                    <div class="filter-item">
                        <a-tooltip :title="$t('automation.insightsPage.statusFilter')">
                            <i class="material-icons filter-icon">filter_list</i>
                        </a-tooltip>
                        <a-select
                            v-model="filters.status"
                            :mode="'multiple'"
                            :placeholder="$t('automation.insightsPage.statusFilter')"
                            style="width: 220px"
                            @change="handleStatusChange"
                        >
                            <a-select-option value="scheduled">
                                <div class="status-option">
                                    <i class="material-icons status-icon scheduled">schedule</i>
                                    <span>{{ $t("automation.insightsPage.statusScheduled") }}</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="running">
                                <div class="status-option">
                                    <i class="material-icons status-icon running">play_arrow</i>
                                    <span>{{ $t("automation.insightsPage.statusRunning") }}</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="waiting-reply">
                                <div class="status-option">
                                    <i class="material-icons status-icon waiting">hourglass_empty</i>
                                    <span>{{ $t("automation.insightsPage.statusWaitingReply") }}</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="done">
                                <div class="status-option">
                                    <i class="material-icons status-icon done">check_circle</i>
                                    <span>{{ $t("automation.insightsPage.statusDone") }}</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="failed">
                                <div class="status-option">
                                    <i class="material-icons status-icon failed">error</i>
                                    <span>{{ $t("automation.insightsPage.statusFailed") }}</span>
                                </div>
                            </a-select-option>
                            <a-select-option value="cancelled">
                                <div class="status-option">
                                    <i class="material-icons status-icon cancelled">cancel</i>
                                    <span>{{ $t("automation.insightsPage.statusCancelled") }}</span>
                                </div>
                            </a-select-option>
                        </a-select>
                    </div>

                    <a-tooltip :title="$t('automation.insightsPage.clearFilters')" v-if="hasActiveFilters()">
                        <a-button class="clear-filters-button" @click="clearAllFilters">
                            <i class="material-icons">clear_all</i>
                        </a-button>
                    </a-tooltip>

                    <a-tooltip :title="$t('automation.insightsPage.refreshData')">
                        <a-button type="primary" @click="refreshData" class="refresh-button">
                            <i class="material-icons">refresh</i>
                        </a-button>
                    </a-tooltip>
                </div>
            </div>

            <div class="active-filters-bar" v-if="hasActiveFilters()">
                <div class="active-filter-info">
                    <i class="material-icons">filter_alt</i>
                    <span>{{ $t("automation.insightsPage.activeFilters") }}</span>
                </div>
                <div class="active-filters-tags">
                    <a-tag v-if="filters.automationId" closable @close="clearAutomationFilter" color="blue">
                        <i class="material-icons">settings_suggest</i>
                        <span>{{ getSelectedAutomationName() }}</span>
                    </a-tag>
                    
                    <a-tag v-if="hasDateFilter()" closable @close="clearDateFilter" color="green">
                        <i class="material-icons">date_range</i>
                        <span>{{ formatDateRange() }}</span>
                    </a-tag>
                    
                    <a-tag v-for="status in filters.status" :key="status" closable @close="() => removeStatus(status)" :color="getStatusColor(status)">
                        <i class="material-icons">{{ getStatusIcon(status) }}</i>
                        <span>{{ getStatusName(status) }}</span>
                    </a-tag>
                </div>
            </div>

            <a-spin :spinning="loading">
                <!-- Genel İstatistikler -->
                <div class="stats-cards">
                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">task_alt</i>
                                {{ $t("automation.insightsPage.totalTasks") }}
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatNumber(insightData.stats.totalTasks) }}</div>
                            <div class="description">{{ $t("automation.insightsPage.executedTasks") }}</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">horizontal_rule</i>
                                {{ $t("automation.insightsPage.successRate") }}
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatPercentage(insightData.stats.successRate) }}%</div>
                            <div class="description">{{ $t("automation.insightsPage.successfulTasks") }}</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">timer</i>
                                {{ $t("automation.insightsPage.avgDuration") }}
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatDuration(insightData.stats.avgDuration) }}</div>
                            <div class="description">{{ $t("automation.insightsPage.avgWorkDuration") }}</div>
                        </div>
                    </a-card>

                    <a-card class="stats-card">
                        <template #title>
                            <div class="card-title">
                                <i class="material-icons">pending</i>
                                {{ $t("automation.insightsPage.activeTasks") }}
                            </div>
                        </template>
                        <div class="card-content">
                            <div class="number">{{ formatNumber(insightData.stats.totalRunningTasks) }}</div>
                            <div class="description">{{ $t("automation.insightsPage.runningNow") }}</div>
                        </div>
                    </a-card>
                </div>

                <!-- Grafikler -->
                <div class="chart-container">
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">timeline</i>
                                        {{ $t("automation.insightsPage.dailyTaskExecution") }}
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="dailyTasksOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">pie_chart</i>
                                        {{ $t("automation.insightsPage.statusDistribution") }}
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="statusDistributionOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                    </a-row>
                </div>

                <div class="chart-container">
                    <a-row :gutter="16">
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">timer</i>
                                        {{ $t("automation.insightsPage.durationDistribution") }}
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="durationDistributionOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                        <a-col :span="12">
                            <a-card class="chart-card">
                                <template #title>
                                    <div class="card-title">
                                        <i class="material-icons">error_outline</i>
                                        {{ $t("automation.insightsPage.terminateReasons") }}
                                    </div>
                                </template>
                                <div class="chart">
                                    <v-chart :options="terminateReasonsOptions" autoresize />
                                </div>
                            </a-card>
                        </a-col>
                    </a-row>
                </div>

                <!-- Otomasyon Performans Tablosu -->
                <a-card class="data-table-card">
                    <template #title>
                        <div class="card-title">
                            <i class="material-icons">leaderboard</i>
                            {{ $t("automation.insightsPage.automationPerformance") }}
                        </div>
                    </template>
                    <a-table
                        :columns="automationPerformanceColumns"
                        :data-source="automationPerformanceData"
                        :pagination="{ pageSize: 10 }"
                        :rowKey="record => record.automationId"
                    >
                        <template #action="{ record }">
                            <a-button type="link" @click="viewAutomationDetails(record.automationId)">
                                {{ $t("automation.insightsPage.view") }}
                            </a-button>
                        </template>
                    </a-table>
                </a-card>
            </a-spin>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import { AUTOMATION_API } from "../../../../../api/automation";
import log2 from "../../../../../utils/log2";

import VChart from "vue-echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/chart/line";
import "echarts/lib/chart/pie";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/title";

const log = log2("AutomationGeneralInsights");

export default {
    name: "AutomationGeneralInsights",
    components: {
        VChart
    },
    data() {
        return {
            loading: false,
            automations: [],
            filters: {
                automationId: null,
                dateRange: [moment().subtract(30, "days"), moment()],
                status: []
            },
            insightData: {
                timeRange: {
                    startDate: null,
                    endDate: null
                },
                dailyTasks: [],
                statusDistribution: [],
                terminateReasons: [],
                stats: {
                    totalTasks: 0,
                    avgDuration: 0,
                    successRate: 0,
                    totalSuccessfulTasks: 0,
                    totalFailedTasks: 0,
                    totalRunningTasks: 0,
                    totalWaitingTasks: 0
                }
            },
            durationStatistics: {
                durationRanges: [],
                automationAvgDuration: [],
                stats: {
                    totalTasksWithDuration: 0,
                    avgDuration: 0,
                    minDuration: 0,
                    maxDuration: 0,
                    totalDuration: 0,
                    avgDurationInSeconds: 0
                }
            },
            statusData: {
                statusDistribution: [],
                dailyPlans: [],
                stats: {
                    totalPlans: 0,
                    scheduledCount: 0,
                    completedCount: 0,
                    cancelledCount: 0,
                    completionRate: 0
                }
            },
            terminateData: {
                terminateReasonsDistribution: [],
                weeklyTerminateReasons: [],
                automationTerminateReasons: []
            },
            statusInfo: {
                scheduled: { key: "statusScheduled", icon: "schedule", color: "blue" },
                running: { key: "statusRunning", icon: "play_arrow", color: "orange" },
                "waiting-reply": { key: "statusWaitingReply", icon: "hourglass_empty", color: "purple" },
                done: { key: "statusDone", icon: "check_circle", color: "green" },
                failed: { key: "statusFailed", icon: "error", color: "red" },
                cancelled: { key: "statusCancelled", icon: "cancel", color: "gray" }
            }
        };
    },
    computed: {
        moment() {
            return moment;
        },
        dateRangePresets() {
            return {
                [this.$t("automation.insightsPage.lastWeek")]: [moment().subtract(7, "days"), moment()],
                [this.$t("automation.insightsPage.lastMonth")]: [moment().subtract(30, "days"), moment()],
                [this.$t("automation.insightsPage.last3Months")]: [moment().subtract(90, "days"), moment()]
            };
        },
        automationPerformanceColumns() {
            return [
                {
                    title: this.$t("automation.insightsPage.automationName"),
                    dataIndex: "automationName",
                    key: "automationName"
                },
                {
                    title: this.$t("automation.insightsPage.avgDuration"),
                    dataIndex: "avgDuration",
                    key: "avgDuration",
                    customRender: (text) => this.formatDuration(text)
                },
                {
                    title: this.$t("automation.insightsPage.executionCount"),
                    dataIndex: "count",
                    key: "count",
                    sorter: (a, b) => a.count - b.count
                },
                {
                    title: this.$t("automation.insightsPage.successRate"),
                    dataIndex: "successRate",
                    key: "successRate",
                    customRender: (text, record) => {
                        const successRate = record.successCount ? (record.successCount / record.count * 100).toFixed(2) : 0;
                        return successRate + "%";
                    }
                },
                {
                    title: this.$t("automation.insightsPage.actions"),
                    key: "action",
                    width: "10%",
                    scopedSlots: { customRender: "action" }
                }
            ];
        },
        dailyTasksOptions() {
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                legend: {
                    data: [
                        this.$t("automation.insightsPage.totalTasksLegend"),
                        this.$t("automation.insightsPage.successfulLegend"),
                        this.$t("automation.insightsPage.failedLegend")
                    ]
                },
                xAxis: {
                    type: 'category',
                    data: this.insightData.dailyTasks.map(item => moment(item.date).format('DD/MM'))
                },
                yAxis: {
                    type: 'value'
                },
                series: [
                    {
                        name: this.$t("automation.insightsPage.totalTasksLegend"),
                        type: "line",
                        data: this.insightData.dailyTasks.map(item => item.count),
                        lineStyle: {
                            color: '#1890ff'
                        }
                    },
                    {
                        name: this.$t("automation.insightsPage.successfulLegend"),
                        type: "bar",
                        stack: "tasks",
                        data: this.insightData.dailyTasks.map(item => item.successCount),
                        itemStyle: {
                            color: '#52c41a'
                        }
                    },
                    {
                        name: this.$t("automation.insightsPage.failedLegend"),
                        type: "bar",
                        stack: "tasks",
                        data: this.insightData.dailyTasks.map(item => item.failedCount),
                        itemStyle: {
                            color: '#f5222d'
                        }
                    }
                ]
            };
        },
        statusDistributionOptions() {
            const statusColors = {
                'scheduled': '#1890ff',
                'running': '#fa8c16',
                'waiting-reply': '#722ed1',
                'done': '#52c41a',
                'failed': '#f5222d',
                'cancelled': '#bfbfbf'
            };

            const statusNames = {
                scheduled: this.$t("automation.insightsPage.statusScheduled"),
                running: this.$t("automation.insightsPage.statusRunning"),
                "waiting-reply": this.$t("automation.insightsPage.statusWaitingReply"),
                done: this.$t("automation.insightsPage.statusDone"),
                failed: this.$t("automation.insightsPage.statusFailed"),
                cancelled: this.$t("automation.insightsPage.statusCancelled")
            };

            return {
                tooltip: {
                    trigger: 'item',
                    formatter: '{b}: {c} ({d}%)'
                },
                legend: {
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    data: this.insightData.statusDistribution.map(item => statusNames[item.status])
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['50%', '70%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false
                        },
                        emphasis: {
                            label: {
                                show: true,
                                fontSize: '18',
                                fontWeight: 'bold'
                            }
                        },
                        labelLine: {
                            show: false
                        },
                        data: this.insightData.statusDistribution.map(item => ({
                            name: statusNames[item.status],
                            value: item.count,
                            itemStyle: {
                                color: statusColors[item.status]
                            }
                        }))
                    }
                ]
            };
        },
        durationDistributionOptions() {
            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    }
                },
                xAxis: {
                    type: 'category',
                    data: this.durationStatistics.durationRanges.map(item => item.range),
                    axisLabel: {
                        interval: 0,
                        rotate: 30
                    }
                },
                yAxis: {
                    type: 'value'
                },
                series: [
                    {
                        name: this.$t("automation.insightsPage.taskCount"),
                        type: "bar",
                        data: this.durationStatistics.durationRanges.map(item => item.count),
                        itemStyle: {
                            color: '#1890ff'
                        }
                    }
                ]
            };
        },
        terminateReasonsOptions() {
            return {
                tooltip: {
                    trigger: 'item',
                    formatter: '{b}: {c} ({d}%)'
                },
                legend: {
                    type: 'scroll',
                    orient: 'vertical',
                    right: 10,
                    top: 'center',
                    data: this.terminateData.terminateReasonsDistribution.map(item => item.reason)
                },
                series: [
                    {
                        type: 'pie',
                        radius: '55%',
                        center: ['40%', '50%'],
                        data: this.terminateData.terminateReasonsDistribution.map(item => ({
                            name: item.reason,
                            value: item.count
                        }))
                    }
                ]
            };
        },
        automationPerformanceData() {
            return this.durationStatistics.automationAvgDuration;
        }
    },
    methods: {
        ...mapActions({}),
        hasActiveFilters() {
            return this.filters.automationId !== null || 
                this.hasDateFilter() || 
                this.filters.status.length > 0;
        },
        getActiveFilterCount() {
            let count = 0;
            if (this.filters.automationId) count++;
            if (this.hasDateFilter()) count++;
            if (this.filters.status.length > 0) count++;
            return count;
        },
        hasDateFilter() {
            if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return false;
            
            const defaultStart = moment().subtract(30, "days").startOf('day');
            const defaultEnd = moment().endOf('day');
            
            const start = moment(this.filters.dateRange[0]).startOf('day');
            const end = moment(this.filters.dateRange[1]).endOf('day');
            
            return !start.isSame(defaultStart, 'day') || !end.isSame(defaultEnd, 'day');
        },
        formatDateRange() {
            if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return '';
            
            return `${moment(this.filters.dateRange[0]).format('DD.MM.YYYY')} - ${moment(this.filters.dateRange[1]).format('DD.MM.YYYY')}`;
        },
        getSelectedAutomationName() {
            if (!this.filters.automationId) return '';
            
            const automation = this.automations.find(a => a._id === this.filters.automationId);
            return automation ? automation.name : '';
        },
        getStatusName(status) {
            const info = this.statusInfo[status];
            return info ? this.$t("automation.insightsPage." + info.key) : status;
        },
        getStatusIcon(status) {
            const info = this.statusInfo[status];
            return info ? info.icon : "help_outline";
        },
        getStatusColor(status) {
            const info = this.statusInfo[status];
            return info ? info.color : "default";
        },
        clearAutomationFilter() {
            this.filters.automationId = null;
            this.fetchData();
        },
        clearDateFilter() {
            this.filters.dateRange = [moment().subtract(30, "days"), moment()];
            this.fetchData();
        },
        clearStatusFilter() {
            this.filters.status = [];
            this.fetchData();
        },
        clearAllFilters() {
            this.filters.automationId = null;
            this.filters.dateRange = [moment().subtract(30, "days"), moment()];
            this.filters.status = [];
            this.fetchData();
        },
        removeStatus(status) {
            this.filters.status = this.filters.status.filter(s => s !== status);
            this.fetchData();
        },
        async fetchAutomations() {
            try {
                const result = await AUTOMATION_API.listAutomations({});
                this.automations = result.items || [];
            } catch (error) {
                log.error("Otomasyonlar listelenirken hata oluştu:", error);
                this.$message.error(this.$t("automation.insightsPage.automationsLoadError"));
            }
        },
        async fetchData() {
            this.loading = true;
            try {
                const filters = {
                    automationId: this.filters.automationId,
                    status: this.filters.status
                };
                const timeRange = {
                    startDate: this.filters.dateRange[0].toISOString(),
                    endDate: this.filters.dateRange[1].toISOString()
                };

                // Tüm veri çağrılarını paralel yapalım
                const [insightData, durationStatistics, terminateData] = await Promise.all([
                    AUTOMATION_API.INSIGHTS.getAutomationInsights(filters, timeRange),
                    AUTOMATION_API.INSIGHTS.getDurationStatistics(filters),
                    AUTOMATION_API.INSIGHTS.getTerminateReasons(filters)
                ]);

                this.insightData = insightData;
                this.durationStatistics = durationStatistics;
                this.terminateData = terminateData;
            } catch (error) {
                log.error("İstatistikler alınırken hata oluştu:", error);
                this.$message.error(this.$t("automation.insightsPage.insightsLoadError"));
            } finally {
                this.loading = false;
            }
        },
        handleAutomationChange(value) {
            this.filters.automationId = value;
            this.fetchData();
        },
        handleDateChange(date) {
            if (date && date.length === 2) {
                this.filters.dateRange = date;
                this.fetchData();
            }
        },
        handleStatusChange(values) {
            this.filters.status = values;
            this.fetchData();
        },
        refreshData() {
            this.fetchData();
        },
        formatNumber(value) {
            return value ? value.toLocaleString() : 0;
        },
        formatPercentage(value) {
            return value ? value.toFixed(2) : 0;
        },
        formatDuration(milliseconds) {
            const ms = this.$t("automation.insightsPage.ms");
            const sec = this.$t("automation.insightsPage.sec");
            const min = this.$t("automation.insightsPage.min");
            const hour = this.$t("automation.insightsPage.hour");
            if (!milliseconds) return "0 " + ms;
            const seconds = milliseconds / 1000;
            if (seconds < 1) {
                return milliseconds.toFixed(0) + " " + ms;
            } else if (seconds < 60) {
                return seconds.toFixed(2) + " " + sec;
            } else if (seconds < 3600) {
                const minutes = Math.floor(seconds / 60);
                const remainingSeconds = seconds % 60;
                return minutes + " " + min + " " + remainingSeconds.toFixed(0) + " " + sec;
            } else {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return hours + " " + hour + " " + minutes + " " + min;
            }
        },
        viewAutomationDetails(automationId) {
            this.$router.push(`/automations/designer/${automationId}`);
        }
    },
    mounted() {
        this.fetchAutomations();
        this.fetchData();
    }
};
</script>

<style lang="scss" scoped>
.automation-general-insights {
    padding: 20px;
    background-color: var(--color-bg-default);
    min-height: calc(100vh - 80px);

    .insights-container {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .header-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 8px;

            .title {
                font-size: 24px;
                font-weight: 700;
                color: var(--color-text-default);
            }

            .filter-container {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
                
                .filter-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    
                    .filter-icon {
                        color: var(--color-primary);
                        font-size: 20px;
                    }
                }
                
                .clear-filters-button, .refresh-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 8px;
                    height: 32px;
                    
                    i {
                        font-size: 20px;
                    }
                }
            }
        }
        
        .active-filters-bar {
            display: flex;
            align-items: center;
            gap: 12px;
            background-color: var(--color-bg-neutral-default);
            padding: 8px 12px;
            border-radius: 6px;
            margin-bottom: 20px;
            
            .active-filter-info {
                display: flex;
                align-items: center;
                gap: 6px;
                color: var(--color-text-secondary);
                
                i {
                    font-size: 18px;
                }
                
                span {
                    font-weight: 600;
                }
            }
            
            .active-filters-tags {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                
                .ant-tag {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-right: 0;
                    
                    i {
                        font-size: 14px;
                    }
                }
            }
        }

        .stats-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .stats-card {
                flex: 1;
                min-width: 200px;

                .card-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    color: var(--color-text-default);

                    i {
                        color: var(--color-primary);
                    }
                }

                .card-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 10px 0;

                    .number {
                        font-size: 28px;
                        font-weight: 700;
                        color: var(--color-primary);
                    }

                    .description {
                        font-size: 14px;
                        color: var(--color-text-secondary);
                        margin-top: 5px;
                    }
                }
            }
        }

        .chart-container {
            margin-top: 20px;

            .chart-card {
                margin-bottom: 20px;

                .card-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    color: var(--color-text-default);

                    i {
                        color: var(--color-primary);
                    }
                }

                .chart {
                    height: 350px;
                    width: 100%;
                }
            }
        }

        .data-table-card {
            margin-top: 20px;

            .card-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                color: var(--color-text-default);

                i {
                    color: var(--color-primary);
                }
            }
        }
    }
}

.status-option {
    display: flex;
    align-items: center;
    gap: 6px;
    
    .status-icon {
        font-size: 16px;
        
        &.scheduled { color: #1890ff; }
        &.running { color: #fa8c16; }
        &.waiting { color: #722ed1; }
        &.done { color: #52c41a; }
        &.failed { color: #f5222d; }
        &.cancelled { color: #bfbfbf; }
    }
}
</style> 